/**
 * 地図検索の文字列入力に対する共通サニタイズ処理。
 *
 * 住居表示 / 登記地番のフォーム入力（ChibanViewer）と URL クエリパラメータ
 * （urlParams/schema.ts）の双方で、外部由来の任意入力から不可視・不正な制御文字を
 * 除去するために用いる。両経路で同一ロジックを共有し、実装のドリフトを防ぐ。
 */

/**
 * セキュリティに直結する不可視文字にマッチする正規表現。
 *   - U+0000–U+001F / U+007F : C0 制御文字 + DEL
 *     （CR/LF によるログ・ヘッダインジェクション、NUL による切り詰め等）
 *   - U+200E/200F, U+202A–U+202E, U+2066–U+2069 : 双方向制御（bidi）
 *     （表示順の偽装 = Trojan Source / CVE-2021-42574）
 *   - U+200B–U+200D, U+2060, U+FEFF : ゼロ幅・BOM
 *     （不可視文字によるフィルタ回避・難読化）
 * 通常空白(U+0020)・全角空白などは対象外で、住所内の正当な空白は保持する。
 */
const CONTROL_CHARS_REGEX =
  // eslint-disable-next-line no-control-regex -- 制御文字の意図的な除去
  /[\u0000-\u001f\u007f\u200b-\u200f\u202a-\u202e\u2060\u2066-\u2069\ufeff]/g;

/**
 * 文字列からセキュリティに直結する不可視文字（C0 制御 / DEL / bidi / ゼロ幅 / BOM）を除去する。
 * 前後空白の除去（trim）は呼び出し側の責務とする。
 */
export const stripControlChars = (value: string): string =>
  value.replace(CONTROL_CHARS_REGEX, "");

/**
 * 検索文字列の正規化（制御文字除去 + Unicode NFC 合成）。
 * macOS のファイル名等は NFD（分解形。例: 「ば」= 「は」+ 結合濁点）でコピーされることがあり、
 * そのまま検索 API に送ると NFC 格納の対象にヒットしない。入口で NFC へ合成して
 * 表示・文字数カウント・検索一致を揃える。フォーム入力・URL クエリの双方で共有する。
 */
export const normalizeSearchText = (value: string): string =>
  stripControlChars(value).normalize("NFC");

/**
 * 見た目の文字の数を返す。
 * `String.length` は UTF-16 コードユニット数のため、サロゲートペア（絵文字）や結合文字を
 * 過大にカウントする。文字数カウンタの表示・上限判定は本関数で見た目と一致させる。
 */
const graphemeSegmenter = new Intl.Segmenter("ja", { granularity: "grapheme" });
export const countGraphemes = (value: string): number =>
  [...graphemeSegmenter.segment(value)].length;

/**
 * 先頭から見た目の文字数で `max` 個までに切り詰める。
 * HTML 標準の `maxLength` は UTF-16 コードユニット基準のため絵文字（サロゲートペア）を
 * 2 文字と数え、見た目基準の文字数カウンタと食い違って入力が早く止まる。
 * 入力制限を `countGraphemes` の表示と一致させるため、native maxLength の代わりに用いる。
 */
export const sliceGraphemes = (value: string, max: number): string => {
  let result = "";
  let count = 0;
  for (const { segment } of graphemeSegmenter.segment(value)) {
    if (count >= max) break;
    result += segment;
    count++;
  }
  return result;
};
